import React, { useState, useContext, useEffect, useRef } from 'react';
import { PlaylistContext } from '../context/PlaylistContext';
import { useFirebase } from '../contexts/FirebaseContext';
import { cleanSentenceText } from '../utils/helpers';
import apiClient, { API_BASE_URL } from '../utils/apiClient';
import logger from '../utils/logger';
import './Card.css';
// Switch to Stories-style clickable sentence + popup definitions
import ClickableSentence from '../components/ClickableSentence';
import processSentenceJisho from '../utils/processSentenceJisho';

const DUMMY_AUDIO_URL = 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3';

function extractFirstSentence(fullText = "") {
    if (!fullText) return "";
    const periodIndex = fullText.indexOf(".");
    return periodIndex === -1 ? fullText.trim() : fullText.slice(0, periodIndex + 1).trim();
}

const incrementSentenceCounter = async (language, getUserIdFn) => {
    try {
        const userId = await getUserIdFn();
        if (userId) {
            apiClient.post('/api/updateSentenceCount', { userId, language, count: 1, isIncrement: true })
                .catch(err => logger.error('Error updating sentence count on server:', err));
        }
    } catch (error) {
        logger.error('Error in updateSentenceCount:', error);
    }
    window.dispatchEvent(new CustomEvent('sentenceCountUpdated', { detail: { action: 'increment', language } }));
};

const Card = (props) => {
    const { sentenceData = {}, onSave, onPlayAudio } = props;
    const { playlists, addToPlaylist, removeFromPlaylist } = useContext(PlaylistContext);
    const { isAuthenticated, getToken, getUserId } = useFirebase();
    const [isSynthesizing, setIsSynthesizing] = useState(false);
    const [isAddingToPlaylist, setIsAddingToPlaylist] = useState(false);
    const [audioUrl, setAudioUrl] = useState('');
    const [showTranslation, setShowTranslation] = useState(false);
    const [playlistError, setPlaylistError] = useState("");
    const [addedToToday, setAddedToToday] = useState(false);
    const [verifiedSavedWords, setVerifiedSavedWords] = useState([]);
    const [isVerifyingWords, setIsVerifyingWords] = useState(false);
    const [knownWords, setKnownWords] = useState(new Set());

    const prevSentenceRef = useRef(sentenceData.targetSentence);
    const shortTranslation = cleanSentenceText(extractFirstSentence(sentenceData.translation));

    useEffect(() => {
        setAudioUrl(sentenceData?.audioSrc || '');
    }, [sentenceData?.audioSrc]);

    useEffect(() => {
        if (sentenceData.targetSentence !== prevSentenceRef.current) {
            setShowTranslation(false);
            prevSentenceRef.current = sentenceData.targetSentence;
            if (!sentenceData.usedSavedWords || sentenceData.usedSavedWords.length === 0) {
                setVerifiedSavedWords([]);
            } else {
                setVerifiedSavedWords([]); // Reset, will be re-verified if translation shown
            }
        }
        setPlaylistError("");
        setAddedToToday(false); // Reset this as well
    }, [sentenceData.targetSentence]); // Only depend on targetSentence changing

    useEffect(() => {
        const isAdded = playlists.some(pl =>
            pl.sentences.some(s => s.targetSentence === sentenceData.targetSentence)
        );
        setAddedToToday(isAdded);
    }, [playlists, sentenceData.targetSentence]);

    useEffect(() => {
        if (showTranslation) { // Verify words only when translation is shown
            if (sentenceData.usedSavedWords && sentenceData.usedSavedWords.length > 0) {
                verifyUserSavedWords();
            } else {
                setVerifiedSavedWords([]);
            }
        }
    }, [showTranslation, sentenceData.usedSavedWords, sentenceData.targetSentence]); // Add sentenceData.targetSentence dependency

    const verifyUserSavedWords = async () => {
        if (isVerifyingWords || !sentenceData.usedSavedWords || sentenceData.usedSavedWords.length === 0) {
            if (isVerifyingWords) logger.debug("Already verifying words, skipping.");
            else if (!sentenceData.usedSavedWords || sentenceData.usedSavedWords.length === 0) logger.debug("No saved words to verify.");
            return;
        }

        const userId = await getUserId();
        if (!userId) {
            logger.error("No user ID found for verifying words");
            return;
        }

        setIsVerifyingWords(true);
        logger.debug("Verifying saved words:", sentenceData.usedSavedWords);

        try {
            const language = props.learningLanguage || localStorage.getItem("learningLanguage") || "Spanish";
            const response = await apiClient.get(`/api/getUserWords?userId=${userId}&language=${language}`);
            const data = await response.json(); // Assuming apiClient handles response.ok and .json()

            const userSavedWordsList = data.words.map(item => item.item.toLowerCase());
            const verified = sentenceData.usedSavedWords.filter(
                word => userSavedWordsList.includes(word.toLowerCase())
            );

            logger.debug("Verified saved words result:", verified);
            // Check if the sentence is still the same one we started verifying for
            if (sentenceData.targetSentence === prevSentenceRef.current) {
                setVerifiedSavedWords(verified);
            } else {
                logger.debug("Sentence changed during verification, discarding results.");
            }
        } catch (error) {
            logger.error("Error verifying saved words:", error);
        } finally {
            setIsVerifyingWords(false);
        }
    };

    const handleWordKnown = async (word) => {
        try {
            const userId = await getUserId();
            const language = props.learningLanguage || localStorage.getItem("learningLanguage") || "Spanish";
            await apiClient.post('/api/updateWordKnown', { userId, language, item: word, known: true });
            setKnownWords(prev => new Set([...prev, word]));
            setVerifiedSavedWords(prev => prev.filter(w => w !== word));
        } catch (error) {
            logger.error('Error updating word known status:', error);
        }
    };

    const handlePlay = async (e, fromPlaylist = false) => {
        if (e && typeof e.stopPropagation === 'function') e.stopPropagation();

        // Construct full URL for playing cached audio
        let fullCachedAudioUrl = '';
        if (audioUrl && audioUrl !== DUMMY_AUDIO_URL) {
            fullCachedAudioUrl = audioUrl.startsWith('http') ? audioUrl : `${API_BASE_URL}${audioUrl}`;
            try {
                const audio = new Audio(fullCachedAudioUrl);
                audio.crossOrigin = "anonymous";
                await audio.play();
                if (onPlayAudio) onPlayAudio();
                return fullCachedAudioUrl; // Return the full URL
            } catch (error) {
                logger.error("Error playing cached audio:", error, "URL:", fullCachedAudioUrl);
                setAudioUrl(''); // Force regeneration if cached play fails
            }
        }

        if (!fromPlaylist) setIsSynthesizing(true);
        try {
            const learningLanguage = props.learningLanguage || localStorage.getItem("learningLanguage") || "Spanish";
            // For Chinese, strip inter-word spaces from the TTS text since Chinese
            // writing doesn't use spaces between words. Gemini adds spaces for word
            // segmentation, but they cause unnatural pauses in TTS audio.
            // Preserve punctuation (period, comma, etc.) for proper intonation.
            let ttsText = sentenceData.targetSentence;
            if (learningLanguage === 'Chinese' && typeof ttsText === 'string') {
                ttsText = ttsText.replace(/\s+/g, '');
            }
            const payload = { text: ttsText, learningLanguage };
            if (learningLanguage === "Spanish") {
                payload.spanishRegion = props.spanishRegion || localStorage.getItem("spanishRegion") || "Latin America";
            }

            const token = isAuthenticated ? await getToken() : localStorage.getItem('access_token');
            if (!token && isAuthenticated) throw new Error('Not authenticated or token unavailable.');

            const data = await apiClient.post('/api/tts', payload, {
                // apiClient handles Authorization header and Content-Type for JSON by default
                // If 'credentials: include' was important, ensure apiClient is configured for it or handle appropriately
            });

            if (!data.audio) { logger.error("No audio URL received."); return false; }

            const relativeAudioUrl = data.audio; // e.g., /audio/filename.wav
            const absoluteAudioUrl = relativeAudioUrl.startsWith('http') ? relativeAudioUrl : `${API_BASE_URL}${relativeAudioUrl}`;

            setAudioUrl(absoluteAudioUrl); // Store the absolute URL
            if (onSave) onSave({ ...sentenceData, audioSrc: absoluteAudioUrl }); // Save absolute URL if needed
            setPlaylistError("");

            const audio = new Audio(absoluteAudioUrl);
            audio.crossOrigin = "anonymous";
            if (!fromPlaylist) {
                await audio.play();
                if (onPlayAudio) onPlayAudio();
            }
            return absoluteAudioUrl;
        } catch (error) {
            logger.error("Error synthesizing audio:", error);
            return false;
        } finally {
            if (!fromPlaylist) setIsSynthesizing(false);
        }
    };

    const handleTogglePlaylist = async () => {
        const currentAudioUrl = audioUrl || '';
        const learningLanguage = props.learningLanguage || localStorage.getItem("learningLanguage") || "Spanish";

        if (addedToToday) {
            const result = await removeFromPlaylist(learningLanguage, { ...sentenceData, audioSrc: currentAudioUrl });
            if (result && !result.success) { setPlaylistError(result.message || "Failed to remove."); return; }
            setAddedToToday(false);
            setPlaylistError("");
            window.dispatchEvent(new CustomEvent('sentenceCountUpdated', { detail: { language: learningLanguage, action: 'remove' } }));
        } else {
            setIsAddingToPlaylist(true);
            setPlaylistError("");
            let audioUrlToUse = currentAudioUrl || await handlePlay(null, true);

            if (!audioUrlToUse) {
                setPlaylistError("Failed to generate audio. Please try again.");
                setIsAddingToPlaylist(false);
                return;
            }

            const result = await addToPlaylist(learningLanguage, { ...sentenceData, audioSrc: audioUrlToUse });
            if (result && !result.success) {
                setPlaylistError(result.message || "Failed to add.");
                setIsAddingToPlaylist(false);
                return;
            }

            setPlaylistError("");
            setAddedToToday(true);
            setIsAddingToPlaylist(false);
            incrementSentenceCounter(learningLanguage, getUserId);
            window.dispatchEvent(new CustomEvent('sentenceCountUpdated', { detail: { language: learningLanguage, action: 'add' } }));
        }
    };

    const containsChinese = sentenceData.targetSentence && /[\u4E00-\u9FFF]/.test(sentenceData.targetSentence);
    const textStyle = containsChinese ? {
        fontFamily: "'Noto Sans SC', 'Noto Sans TC', 'PingFang SC', 'Microsoft YaHei', sans-serif",
        lineHeight: "1.5",
        fontWeight: "normal"
    } : {};

    // Determine current learning language for clickable processing
    const learningLanguage = props.learningLanguage || localStorage.getItem("learningLanguage") || "Spanish";

    // GENERATE UNIQUE LOOKUP KEY: Adding a snippet of the sentence text ensures 
    // the keys are totally unique to this specific card, forcing React to remount child components!
    const safeSentenceForKey = typeof sentenceData.targetSentence === 'string'
        ? sentenceData.targetSentence.replace(/[^a-zA-Z0-9\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF]/g, '').substring(0, 10)
        : 'empty';
    const lookupKey = `card-${learningLanguage}-${safeSentenceForKey}`;

    // Prepare sentence content using Stories-style processing
    const clickableContent = (learningLanguage === 'Japanese' || learningLanguage === 'Chinese')
        ? processSentenceJisho(sentenceData.targetSentence || '', lookupKey)
        : (Array.isArray(sentenceData.targetSentence) ? sentenceData.targetSentence : (sentenceData.targetSentence || ''));

    return (
        <div className="card-container">
            {/* Use Stories-style ClickableSentence which shows a small popup near the word */}
            <div className="card-sentence" style={textStyle}>
                <ClickableSentence
                    sentence={clickableContent}
                    lookupKey={lookupKey}
                    language={learningLanguage}
                    lemmatizeSavedWord={true}
                />
            </div>

            <div className="card-top-button-row">
                <button className="card-play-button" onClick={handlePlay} disabled={isSynthesizing}>
                    {isSynthesizing ? 'Synthesizing...' : 'Play'}
                </button>
                <button className="card-add-button" onClick={handleTogglePlaylist} disabled={isSynthesizing || isAddingToPlaylist}>
                    {isAddingToPlaylist ? 'Processing...' : (addedToToday ? '- Remove from Playlist' : '+ Add to Playlist')}
                </button>
            </div>

            {playlistError && <p className="playlist-error">{playlistError}</p>}

            <div className="card-toggle-row">
                <button className="card-toggle-button" onClick={() => setShowTranslation(prev => !prev)}>
                    {showTranslation ? 'Hide Translation' : 'Show Translation'}
                </button>
            </div>

            {showTranslation && (
                <div className="card-translation">
                    <p className="card-translation-text" style={textStyle}>{shortTranslation}</p>
                    <hr className="card-separator" />
                    {sentenceData.definitions && (
                        <div style={textStyle}>
                            {renderDefinitions(sentenceData.definitions)}
                        </div>
                    )}

                    {verifiedSavedWords && verifiedSavedWords.length > 0 && (
                        <>
                            <hr className="card-separator" />
                            <div className="card-saved-words" style={textStyle}>
                                <h4 className="card-saved-words-title">Your Saved Words:</h4>
                                <div className="saved-words-list">
                                    {verifiedSavedWords.map((word, index) => (
                                        !knownWords.has(word) && (
                                            <div key={`${word}-${index}`} className="saved-word-item">
                                                <span className="saved-word">{word}</span>
                                                <label className="word-known-label">
                                                    <input type="checkbox" onChange={() => handleWordKnown(word)} />
                                                    <span>I know this word now.</span>
                                                </label>
                                            </div>
                                        )
                                    ))}
                                </div>
                            </div>
                        </>
                    )}

                    {sentenceData.grammarExplanation && (
                        <>
                            <hr className="card-separator" />
                            <div className="card-grammar-explanation" style={textStyle}>
                                <h4>Grammar Point:</h4>
                                {typeof sentenceData.grammarExplanation === 'object' && sentenceData.grammarExplanation !== null ? (
                                    Object.entries(sentenceData.grammarExplanation).map(([term, explanation], index) => (
                                        <div key={index}><strong>{term}:</strong> {explanation}</div>
                                    ))
                                ) : (<p>{String(sentenceData.grammarExplanation)}</p>)}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

// Moved below component so it can be used inside
const renderDefinitions = (defs) => {
    const ErrorNotice = ({ message = "There may be display issues with these definitions. Please try reloading." }) => (
        <p className="card-error-notice" style={{
            color: '#e74c3c', fontSize: '14px', marginTop: '10px',
            padding: '8px', backgroundColor: '#ffebee', borderRadius: '4px'
        }}>
            {message}
        </p>
    );

    if (!defs || !Array.isArray(defs)) { // Expecting an array
        logger.warn("Definitions format is not an array as expected:", defs);
        return <ErrorNotice message="Definitions format error. Expected an array." />;
    }
    if (defs.length === 0) {
        return <p className="card-text">No definitions available.</p>;
    }

    return (
        <div>
            <ul className="card-list">
                {defs.map((item, index) => {
                    if (typeof item === 'object' && item !== null && typeof item.word === 'string') {
                        const definitionsText = Array.isArray(item.definitions)
                            ? item.definitions.join(', ')
                            : String(item.definitions || 'N/A');

                        const grammaticalInfo = item.verb_info || item.note || item.partOfSpeech || '';

                        return (
                            <li key={`${item.word}-${index}`} className="card-definition-item">
                                <strong>{item.word}</strong>
                                {item.pinyin && <span className="card-pinyin"> [{item.pinyin}]</span>}
                                {item.romaji && <span className="card-pinyin"> [{item.romaji}]</span>}
                                <span>: {definitionsText}</span>
                                {grammaticalInfo && <span className="card-definition-note"> ({grammaticalInfo.trim()})</span>}
                            </li>
                        );
                    }
                    // Log and display malformed item for debugging
                    logger.warn("Malformed definition item:", item);
                    return (
                        <li key={`error-${index}`} className="card-list-item card-definition-error-item" style={{ color: 'red' }}>
                            Unexpected definition item format: {JSON.stringify(item)}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default Card;